import { StatusData } from "@fairfleet/geotab";
import { keepNewestPerKey, NewestEntry } from "@/pages/live-data/utilities/newestReading";

/** Device id → diagnostic id → the newest reading seen for that pair within the scanned window. */
export type LatestReadings = Map<string, Map<string, NewestEntry<StatusData>>>;

/**
 * Folds one page of readings into the newest-per-diagnostic view of a device.
 *
 * Only the newest reading per diagnostic is kept, because the question the page answers is
 * "did this vehicle report this value at all within the window", not "what were all its values".
 * Keeping every reading would hold tens of thousands of rows per vehicle for no gain.
 *
 * A page can arrive out of order — a truncated page is re-requested and the requests run
 * concurrently — so an older reading must not displace a newer one that already arrived; a
 * reading with an unreadable timestamp is not kept.
 *
 * @param into - The view to fold into; mutated in place, because a fleet-sized scan copies far
 * too much otherwise.
 * @param deviceId - The device the page belongs to.
 * @param page - The readings.
 * @returns How many readings were taken in, for progress reporting.
 */
export function mergeLatestReadings(
  into: LatestReadings,
  deviceId: string,
  page: StatusData[],
): number {
  const withDiagnostic = page.filter(reading => reading.diagnostic?.id);
  if (withDiagnostic.length === 0) {
    return 0;
  }

  let byDiagnostic = into.get(deviceId);
  if (byDiagnostic === undefined) {
    byDiagnostic = new Map();
    into.set(deviceId, byDiagnostic);
  }

  keepNewestPerKey(
    byDiagnostic,
    withDiagnostic,
    reading => reading.diagnostic?.id ?? "",
    reading => reading.dateTime,
  );

  return withDiagnostic.length;
}
